import { FunctionComponent, ReactElement, useEffect, useMemo, useState } from "react";
import CustomModal from "../../../shared/Modals/CustomModal";
import { useClaim } from "../../../context-providers/claims-provider";
import { Skeleton, Text } from "@chakra-ui/react";
import DataCardDisplay from "../../../shared/DataDisplay/DataDisplay";
import {
	adjustDatePickerOffset,
	createExternalLinkFromEnv,
	doesUserHaveRoles,
	toLocalISODateString
} from "../../../utils/helpers";
import { emptyFunc, EnvKeys, UserRole } from "../../../utils/consts";
import { useClaimDetails } from "../../../context-providers/claims-details-provider";
import { Claim, User } from "../../../utils/types";
import { useAuth } from "../../../hooks/useAuth";
import DatePicker from "../../../shared/DetailElement/Elements/DatePicker";
import { cloneDeep, set } from "lodash";
import useClaimsToast from "../../../hooks/useToast";
import { useParams } from "react-router-dom";

interface BasicInfoEditModalProps {
	isOpen: boolean;
	onClose(): void;
	onSubmit(): void;
}

enum DatePickerIdentifier {
	DateOfInitialConsultation = "dateOfInitialConsultation",
	FinalDateOfService = "finalDateOfService",
	ReceivedOn = "receivedOn"
}

const BasicInfoEditModal: FunctionComponent<BasicInfoEditModalProps> = (
	props: BasicInfoEditModalProps
): ReactElement => {
	const { id } = useParams();
	const { isOpen, onClose, onSubmit } = props;
	const { getUser } = useAuth();
	const { claim, claimCase, isPremiumPaid, handleUpdateClaimDueDate, handleUpdateClaimCleanDate } = useClaimDetails();
	const { localClaim, setLocalClaim, handleFetchClaim, handleClaimUpsert } = useClaim();
	const toast = useClaimsToast();
	const [newDOIC, setNewDOIC] = useState<string>(
		localClaim.dateOfInitialConsultation.toISOString().split("T")[0] || ""
	);
	const [newFDOC, setNewFDOC] = useState<string>(localClaim.finalDateOfService.toISOString().split("T")[0] || "");
	const [newReceivedOn, setNewReceivedOn] = useState<string>(
		localClaim.receivedOn ? localClaim.receivedOn.toISOString().split("T")[0] : ""
	);
	const [dueDate, setDueDate] = useState<Date | undefined>();
	const [cleanDate, setCleanDate] = useState<Date | undefined>();

	const validationError = useMemo(() => {
		if (cleanDate) {
			if (claim?.receivedOn && cleanDate < new Date(claim.receivedOn.toISOString().split("T")[0])) {
				return "Clean date must be at or after the Received On date";
			}
			if (cleanDate > new Date()) {
				return "Clean date cannot be a future date";
			}
		}
		if (dueDate && dueDate < new Date(new Date().setUTCHours(0, 0, 0, 0))) {
			return "Due date cannot be a past date";
		}
	}, [cleanDate, dueDate, claim]);

	const getDueDate = (): ReactElement => {
		const currentUser: User | undefined = getUser();
		let fragment: ReactElement = <></>;

		if (currentUser) {
			const isManager: boolean = doesUserHaveRoles(currentUser, [UserRole.ClaimsManager]);

			if (isManager) {
				fragment = (
					<DatePicker
						value={dueDate?.toISOString().split("T")[0] ?? claim?.dueDate?.toISOString().split("T")[0] ?? ""}
						min={new Date().toISOString().split("T")[0]}
						onDateChange={(newDate) => setDueDate(new Date(newDate))}
						onDateBlur={() => emptyFunc}
						maxH="24px"
					/>
				);
			} else {
				fragment = (
					<Text color="gray.800" fontSize="16px" fontWeight="400" lineHeight="1.5" ml="4px">
						{claim?.dueDate?.toLocaleDateString() ?? ""}
					</Text>
				);
			}
		}

		return fragment;
	};

	const getCleanDate = (): ReactElement => {
		// Mutation is only allowed for managers and assigned analysts
		const currentUser: User | undefined = getUser();
		const isManager: boolean = currentUser ? doesUserHaveRoles(currentUser, [UserRole.ClaimsManager]) : false;
		const isMutationAllowed =
			currentUser &&
			(isManager ||
				(typeof claim?.assignedAnalyst?.getId === "function" && currentUser.id === claim.assignedAnalyst.getId()));
		return isMutationAllowed ? (
			<DatePicker
				value={cleanDate?.toISOString().split("T")[0] ?? claim?.cleanDate?.toISOString().split("T")[0] ?? ""}
				min={claim?.receivedOn ? claim.receivedOn.toISOString().split("T")[0] : ""}
				max={new Date().toISOString().split("T")[0]}
				onDateChange={(newDate) => setCleanDate(new Date(newDate))}
				onDateBlur={() => emptyFunc}
				disabled={!isMutationAllowed}
				maxH="24px"
			/>
		) : (
			<Text color="gray.800" fontSize="16px" fontWeight="400" lineHeight="1.5" ml="4px">
				{claim?.cleanDate?.toLocaleDateString() ?? ""}
			</Text>
		);
	};

	const handleDateBlur = (propName: DatePickerIdentifier): void => {
		try {
			let newDate: Date = new Date();

			switch (propName) {
				case DatePickerIdentifier.DateOfInitialConsultation:
					newDate = new Date(newDOIC);
					break;
				case DatePickerIdentifier.FinalDateOfService:
					newDate = new Date(newFDOC);
					break;
				case DatePickerIdentifier.ReceivedOn:
					newDate = new Date(newReceivedOn);
					break;
				default:
					console.error(`Tried to deserialize an unknown date element: ${propName}`);
					break;
			}

			let workingCopyClone: Claim = cloneDeep(localClaim);
			workingCopyClone = set(workingCopyClone, propName, adjustDatePickerOffset(newDate));
			setLocalClaim(workingCopyClone);
		} catch (err) {
			console.error(err);
			toast("Error", "date parsing failed", "error");

			setNewDOIC(toLocalISODateString(localClaim.dateOfInitialConsultation));
			setNewFDOC(toLocalISODateString(localClaim.finalDateOfService));

			if (localClaim.receivedOn) {
				setNewReceivedOn(toLocalISODateString(localClaim.receivedOn));
			}
		}
	};

	const handleSubmit = async () => {
		let updates: Array<Promise<void | boolean>> = [];
		if (cleanDate) {
			updates.push(handleUpdateClaimCleanDate(cleanDate));
		}
		if (dueDate) {
			updates.push(handleUpdateClaimDueDate(dueDate));
		}
		if (
			claim?.dateOfInitialConsultation.toDateString() !== localClaim.dateOfInitialConsultation.toDateString() ||
			claim.finalDateOfService.toDateString() !== localClaim.finalDateOfService.toDateString() ||
			claim.receivedOn?.toDateString() !== localClaim.receivedOn?.toDateString()
		) {
			updates.push(handleClaimUpsert(localClaim, false));
		}

		// If no updates, close
		if (updates.length === 0) {
			onClose();
			return;
		}
		// Promise.all resolves to an array (always truthy), so inspect each result:
		// handleClaimUpsert resolves to `false` when the upsert is rejected (e.g. a date
		// change that would alter the policy). Treat any explicit `false` as a failure so
		// the underlying error surfaces instead of a misleading success toast.
		const results = await Promise.all(updates);
		const isSuccess = results.every((result) => result !== false);
		if (isSuccess) {
			toast(undefined, "Claim information updated successfully", "success");
			onSubmit();
		} else {
			toast(undefined, "Updating claim information failed", "error");
		}
	};

	useEffect(() => {
		if (!claim) {
			handleFetchClaim(+(id ?? "0")).then((fetchedClaim) => {
				setNewDOIC(fetchedClaim?.dateOfInitialConsultation.toISOString().split("T")[0] || "");
				setNewFDOC(fetchedClaim?.finalDateOfService.toISOString().split("T")[0] || "");
				setNewReceivedOn(fetchedClaim?.receivedOn ? fetchedClaim.receivedOn.toISOString().split("T")[0] : "");
			});
		}
	}, []);

	// The service dates (Initial Consultation / Final Date of Service) may be edited by a
	// Claims Manager or by the analyst assigned to this claim. The backend applies the same
	// policy-change guard to both. (Received On stays manager-only.)
	const currentUser: User | undefined = getUser();
	const canEditServiceDates: boolean =
		!!currentUser &&
		(doesUserHaveRoles(currentUser, [UserRole.ClaimsManager]) || currentUser.id === claim?.assignedAnalyst?.getId());

	return isOpen ? (
		<CustomModal
			isOpen={isOpen}
			isCentered={true}
			closeOnOverlayClick={false}
			onCancel={onClose}
			onContinue={handleSubmit}
			size="4xl"
			title="Edit Basic Info"
			continueText="Update"
			continueDisabled={!!validationError}
			continueTooltip={validationError}
			content={
				<Skeleton isLoaded={!!claim} endColor="gray.200">
					<DataCardDisplay
						data={[
							{
								title: "PASS Case",
								value: `#${claim?.caseId}`,
								isurl: true,
								url: createExternalLinkFromEnv(EnvKeys.PassUrl, claim?.caseId || "")
							},
							{
								title: "LAMP Case",
								value: `#${claimCase?.getLampcaseid()}`,
								isurl: true,
								url: createExternalLinkFromEnv(EnvKeys.LampCaseUrl, claimCase?.getLampcaseid() || "")
							},
							{
								title: "Member",
								value: `${claim?.member?.getName()} #${claim?.member?.getId()}`,
								isurl: true,
								url: createExternalLinkFromEnv(EnvKeys.MemberPassUrl, claim?.member?.getId() || "")
							},
							{
								title: "Attorney",
								value: `${claim?.atty?.getName()} #${claim?.atty?.getId()}`,
								isurl: true,
								url: createExternalLinkFromEnv(EnvKeys.LampAttorneyUrl, claim?.atty?.getId() || "")
							},
							{
								title: "Date of Initial Consultation",
								value: "", //claim ? `${new Date(claim.dateOfInitialConsultation).toLocaleDateString()}` : "",
								childElements: canEditServiceDates ? (
									<DatePicker
										value={newDOIC}
										max={toLocalISODateString(new Date())}
										onDateChange={(newDate) => setNewDOIC(newDate)}
										onDateBlur={() => handleDateBlur(DatePickerIdentifier.DateOfInitialConsultation)}
										maxH="24px"
									/>
								) : (
									<>{claim ? new Date(claim.dateOfInitialConsultation).toLocaleDateString() : ""}</>
								)
							},
							{
								title: "Final Date of Service",
								value: "", //claim ? new Date(claim.finalDateOfService).toLocaleDateString() : ""
								childElements: canEditServiceDates ? (
									<DatePicker
										value={newFDOC}
										max={toLocalISODateString(new Date())}
										onDateChange={(newDate) => setNewFDOC(newDate)}
										onDateBlur={() => handleDateBlur(DatePickerIdentifier.FinalDateOfService)}
										maxH="24px"
									/>
								) : (
									<>{claim ? new Date(claim.finalDateOfService).toLocaleDateString() : ""}</>
								)
							},
							{
								title: "Received On",
								value: "",
								childElements: doesUserHaveRoles(getUser(), [UserRole.ClaimsManager]) ? (
									<DatePicker
										value={newReceivedOn}
										max={toLocalISODateString(new Date())}
										onDateChange={(newDate) => setNewReceivedOn(newDate)}
										onDateBlur={() => handleDateBlur(DatePickerIdentifier.ReceivedOn)}
										maxH="24px"
									/>
								) : (
									<>{claim?.receivedOn ? new Date(claim.receivedOn).toLocaleDateString() : ""}</>
								)
							},
							{
								title: "Due Date",
								value: "",
								childElements: getDueDate()
							},
							{
								title: "Submitted",
								value: claim?.submittedAt ? new Date(claim.submittedAt).toLocaleDateString() : ""
							},
							{
								title: "Approved",
								value: claim?.approvedAt ? new Date(claim.approvedAt).toLocaleDateString() : ""
							},
							{
								title: "Clean Date",
								value: "",
								childElements: getCleanDate()
							}
						]}
						style={{ width: "100%" }}
						leftLabelMinWidth={100}
						rightLabelMinWidth={210}
					/>
				</Skeleton>
			}
		/>
	) : (
		<></>
	);
};

export default BasicInfoEditModal;
